import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client';
import { uploadEvidence } from '../views/EvidenceInput';
import { useAppServices } from '../views/ViewContext';

/**
 * Preuve photo prise dans le formulaire (ADR-141 §3 bis, même parcours que la tablette) : la caméra
 * s'ouvre dans la page (`getUserMedia`), avec son aperçu et un déclencheur ; chaque prise est
 * compressée (2048 px au plus, JPEG 0,82), déposée et enregistrée comme preuve du sujet, puis
 * ajoutée en vignette ; la caméra reste ouverte jusqu'au plafond (« n / max »). Une vignette se
 * reprend (même place) ou se supprime. Caméra refusée ou absente : le motif s'affiche, et le
 * téléversement d'un fichier reste en secours. Le serveur vérifie le nombre et la nature des preuves.
 */

/** Plus grand côté d'une photo déposée, et qualité JPEG (comme la tablette). */
export const PHOTO_MAX_SIDE = 2048;
export const PHOTO_QUALITY = 0.82;
/** Plafond sans `evidence.max_count` déclaré. */
export const PHOTO_MAX_DEFAULT = 10;

/** Dimensions d'une image ramenée à `max` pixels sur son plus grand côté, proportions gardées. */
export function fitWithin(width: number, height: number, max = PHOTO_MAX_SIDE): { width: number; height: number } {
  if (width <= 0 || height <= 0) return { width: 0, height: 0 };
  const scale = Math.min(1, max / Math.max(width, height));
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/** Caméra du poste, remplaçable par une doublure en test. */
export interface CameraDevice {
  /** Ouvre la caméra (arrière de préférence) ; rejette avec le motif du navigateur. */
  open(): Promise<MediaStream>;
  /** Image courante de l'aperçu, compressée. */
  grab(video: HTMLVideoElement): Promise<Blob>;
  /** Compression d'un fichier choisi en secours ; l'original si le navigateur ne sait pas la faire. */
  compress(file: File): Promise<Blob>;
}

function drawToJpeg(source: CanvasImageSource, width: number, height: number): Promise<Blob> {
  const size = fitWithin(width, height);
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  const context = canvas.getContext('2d');
  if (context === null) return Promise.reject(new Error('Le navigateur ne sait pas préparer la photo.'));
  context.drawImage(source, 0, 0, size.width, size.height);
  return new Promise((resolve, reject) => canvas.toBlob((blob) => (blob === null ? reject(new Error('La photo n’a pas pu être préparée.')) : resolve(blob)), 'image/jpeg', PHOTO_QUALITY));
}

export const browserCamera: CameraDevice = {
  async open() {
    if (typeof navigator === 'undefined' || navigator.mediaDevices?.getUserMedia === undefined) {
      throw Object.assign(new Error('Aucune caméra disponible.'), { name: 'NotFoundError' });
    }
    return navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
  },
  grab(video) {
    return drawToJpeg(video, video.videoWidth, video.videoHeight);
  },
  async compress(file) {
    if (!file.type.startsWith('image/') || typeof createImageBitmap !== 'function') return file;
    try {
      // L'orientation EXIF est appliquée au décodage.
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return await drawToJpeg(bitmap, bitmap.width, bitmap.height);
    } catch {
      return file;
    }
  },
};

/** Motif lisible d'un refus de la caméra. */
export function cameraProblem(error: unknown): string {
  const name = error instanceof Error || (typeof error === 'object' && error !== null && 'name' in error) ? String((error as { name: unknown }).name) : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'Accès à la caméra refusé : autorisez-la dans le navigateur, ou joignez une photo.';
  if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'NotReadableError') return 'Aucune caméra disponible sur ce poste : joignez une photo.';
  return `La caméra n’a pas pu s’ouvrir${error instanceof Error && error.message !== '' ? ` (${error.message})` : ''} : joignez une photo.`;
}

function objectUrl(blob: Blob): string | undefined {
  return typeof URL.createObjectURL === 'function' ? URL.createObjectURL(blob) : undefined;
}

export function CameraEvidence({
  id,
  label,
  value,
  onChange,
  disabled,
  subject,
  maxCount = PHOTO_MAX_DEFAULT,
  camera = browserCamera,
}: {
  id: string;
  label: string;
  value: readonly string[];
  onChange: (ids: string[]) => void;
  disabled: boolean;
  subject: { type: string; id: string } | undefined;
  maxCount?: number;
  camera?: CameraDevice;
}) {
  const services = useAppServices();
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const [live, setLive] = useState(false);
  const [opening, setOpening] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thumbs, setThumbs] = useState<Readonly<Record<string, string | undefined>>>({});
  const [selected, setSelected] = useState<number | null>(null);
  const [retake, setRetake] = useState<number | null>(null);
  const full = value.length >= maxCount;
  const blocked = disabled || subject === undefined;

  const stop = (): void => {
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
    setLive(false);
  };
  useEffect(() => stop, []);
  useEffect(() => {
    if (!live || video.current === null || stream.current === null) return;
    video.current.srcObject = stream.current;
    try {
      void video.current.play()?.catch(() => undefined);
    } catch {
      // Lecture automatique refusée : l'aperçu démarre au premier geste.
    }
  }, [live]);

  const open = async (retakeIndex: number | null = null): Promise<void> => {
    setRetake(retakeIndex);
    setSelected(null);
    setProblem(null);
    if (stream.current !== null) return;
    setOpening(true);
    try {
      stream.current = await camera.open();
      setLive(true);
    } catch (caught) {
      setProblem(cameraProblem(caught));
    } finally {
      setOpening(false);
    }
  };

  const keep = async (blob: Blob, index: number | null): Promise<void> => {
    if (subject === undefined) return;
    setBusy(true);
    setError(null);
    try {
      const named = blob instanceof File ? blob : Object.assign(blob, { name: `photo-${Date.now()}.jpg` });
      const evidenceId = await uploadEvidence(services.client, named, subject, 'photo');
      setThumbs((current) => ({ ...current, [evidenceId]: objectUrl(blob) }));
      const next = [...value];
      if (index !== null && index < next.length) next[index] = evidenceId;
      else next.push(evidenceId);
      onChange(next);
      if (index !== null || next.length >= maxCount) stop();
      setRetake(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  const shoot = async (): Promise<void> => {
    if (video.current === null) return;
    try {
      await keep(await camera.grab(video.current), retake);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  };

  const remove = (index: number): void => {
    setSelected(null);
    onChange(value.filter((_, position) => position !== index));
  };

  return (
    <div className="ay-camera" id={id} role="group" aria-label={label}>
      <div className="ay-camera__head">
        <span className="ay-camera__count" aria-live="polite">
          {`${value.length} / ${maxCount}`}
        </span>
        {subject === undefined && <span className="ay-text ay-text--muted">Preuve possible une fois la tâche rattachée à son objet.</span>}
      </div>
      <ul className="ay-camera__thumbs" aria-label="Photos prises">
        {value.map((evidenceId, index) => (
          <li key={`${evidenceId}-${index}`} className="ay-camera__thumb-item">
            <button type="button" className={`ay-camera__thumb${selected === index ? ' ay-camera__thumb--selected' : ''}`} aria-label={`Photo ${index + 1}`} aria-expanded={selected === index} disabled={blocked || busy} onClick={() => setSelected(selected === index ? null : index)}>
              {thumbs[evidenceId] !== undefined ? <img src={thumbs[evidenceId]} alt="" /> : <span>Photo {index + 1}</span>}
            </button>
            {selected === index && (
              <span className="ay-camera__thumb-actions">
                <button type="button" className="ay-button ay-button--secondary" onClick={() => void open(index)}>
                  Reprendre
                </button>
                <button type="button" className="ay-button ay-button--secondary" onClick={() => remove(index)}>
                  Supprimer
                </button>
              </span>
            )}
          </li>
        ))}
        {!full && !live && (
          <li className="ay-camera__thumb-item">
            <button type="button" className="ay-camera__thumb ay-camera__thumb--add" disabled={blocked || opening} aria-busy={opening} onClick={() => void open(null)}>
              {opening ? 'Ouverture…' : 'Prendre une photo'}
            </button>
          </li>
        )}
      </ul>
      {live && (
        <div className="ay-camera__live">
          <video ref={video} className="ay-camera__preview" autoPlay playsInline muted aria-label="Aperçu de la caméra" />
          <div className="ay-camera__controls">
            <button type="button" className="ay-camera__shutter" aria-label={retake === null ? 'Prendre la photo' : `Reprendre la photo ${retake + 1}`} disabled={busy || (retake === null && full)} aria-busy={busy} onClick={() => void shoot()} />
            <button type="button" className="ay-button ay-button--secondary" onClick={stop}>
              Fermer la caméra
            </button>
          </div>
        </div>
      )}
      {busy && <span className="ay-text ay-text--muted">Dépôt de la photo…</span>}
      {problem !== null && (
        <div className="ay-camera__problem" role="alert">
          <span>{problem}</span>
          <button type="button" className="ay-button ay-button--secondary" onClick={() => void open(retake)}>
            Réessayer
          </button>
          <label className="ay-camera__fallback">
            <span>Joindre une photo</span>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              disabled={blocked || busy || (retake === null && full)}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file !== undefined) void camera.compress(file).then((blob) => keep(blob, retake));
              }}
            />
          </label>
        </div>
      )}
      {error !== null && (
        <span className="ay-field__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
